import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import UserRoles from "../../model/enums/UserRole";
import AlterationForm from "../../model/AlterationForm";
import AlterationFormsListElement from "./AlterationFormsListElement";
import AlterationFormsListAddElement from "./AlterationFormsListAddElement";
import { useAlerts } from "../../contexts/AlertsContext";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

/*
  Component to manage the display of the existing SITES list
*/
function AlterationFormsList() {
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<AlterationForm[]>([]);
  // manage data loading animation
  const [loading, setLoading] = useState(true);
  // manage alert messages
  const alertsManager = useAlerts();

  const refreshList = () => {
    setLoading(true);
    apiClient
      ?.get<PagedData<AlterationForm[]>>({
        url: APIEndPoints.ALTERATION_FORMS.ALL,
        requiresToken: true,
      })
      .then((response: PagedData<AlterationForm[]>) => {
        setData(response.items.reverse());
      })
      .catch(() => {
        // we rely on default API error management
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteElement = (id: number, type: string) => {
    const url =
      type == "alteration_form"
        ? APIEndPoints.ALTERATION_FORMS.ONE(id)
        : APIEndPoints.ALTERATIONS.ONE(id);
    apiClient
      ?.delete({ url: url, requiresToken: true })
      .then(() => {
        // inform the user that the delete has been successfull
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record deleted successfully",
        });
        // refresh the data from the server
        refreshList();
      })
      .catch((error: IApiClientError) => {
        console.log(error);
      });
  };

  const createElement = (
    name: string,
    type: string,
    idAlterationForm?: number | undefined,
  ) => {
    const url =
      type == "alteration_form"
        ? APIEndPoints.ALTERATION_FORMS.NEW
        : APIEndPoints.ALTERATIONS.NEW;

    const body =
      type == "alteration_form"
        ? { name: name }
        : { name: name, id_alteration_form: idAlterationForm };

    apiClient
      ?.post({
        url: url,
        body: body,
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record created successfully",
        });
        refreshList();
      })
      .catch((error: IApiClientError) => {
        console.log(error);
      });
  };

  const updateElement = (newName: string, id: number, type: string) => {
    const url =
      type == "alteration_form"
        ? APIEndPoints.ALTERATION_FORMS.ONE(id)
        : APIEndPoints.ALTERATIONS.ONE(id);

    apiClient
      ?.put({
        url: url,
        body: { name: newName },
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record updated successfully",
        });
        refreshList();
      })
      .catch((error: IApiClientError) => {
        console.log(error);
      });
  };

  // load record data from the server
  useEffect(() => {
    refreshList();
  }, [apiClient]);

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    authManager.getUserRole() === UserRoles.ADMIN &&
    !EmbeddingToolInfo.isChemiTool();

  return (
    <div>
      {canEdit && (
        <div className="alteration-form">
          <div className="details-section-content">
            <AlterationFormsListAddElement onCreate={createElement} />
          </div>
        </div>
      )}

      <div>
        {data?.length > 0 &&
          data?.map((item) => {
            return (
              <AlterationFormsListElement
                key={item.id}
                data={item}
                canEdit={canEdit}
                onDelete={deleteElement}
                onCreate={createElement}
                onUpdate={updateElement}
              />
            );
          })}

        {data?.length === 0 && (
          <div className="details-no-records">
            No Alteration Forms available.
          </div>
        )}
      </div>
    </div>
  );
}

export default AlterationFormsList;
